import { useCreateFindingMutation } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { ConflictCategory, ConflictType, FindingStatus, ProcessorName } from '@consigli/types';
import { useState } from 'react';

import { SheetForm, Field, Select } from '@/components';
import { errorToast, successToast } from '@/components/toast';

import { ConflictFieldset } from './conflict-fieldset';
import { DocumentFindingForm } from './document-finding-form';
import { FindingCreationData, FindingFormData, FormErrors } from './types';
import { validateFindingForm } from './validator';

interface CreateFindingFormProps {
  projectId: number;
  packageId: string;
}

export const CreateFindingForm = ({ projectId, packageId }: CreateFindingFormProps) => {
  const [formData, setFormData] = useState<FindingFormData>({
    findingType: 'DOCUMENT',
    selectedDocument: null,
    textInDocument: null,
    pageNumber: null,
    conflictMessage: null,
    conflictMessageEn: null,
    conflictType: null,
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const [createFinding] = useCreateFindingMutation();

  const handleSubmit = async (): Promise<boolean> => {
    const validation = validateFindingForm(formData);
    setErrors(validation.errors);

    if (!validation.isValid) {
      return false;
    }
    try {
      let findingData: FindingCreationData = {
        processor_name: ProcessorName.HUMAN_INTELLIGENCE,
        message_template: 'FT001',
        message_context: { CUSTOM_MESSAGE: formData.conflictMessage! },
        message_context_en: { CUSTOM_MESSAGE: formData.conflictMessageEn! },
        status: FindingStatus.ACTIVE,
        conflict_message: formData.conflictMessage!,
        conflict_type: formData.conflictType as ConflictType,
        conflict_category: ConflictCategory.INSIGHT,
        content: null,
        page_number: null,
        blob_id: null,
        download_url: null,
      };
      if (formData.findingType === 'DOCUMENT') {
        findingData = {
          ...findingData,
          content: formData.textInDocument,
          page_number: formData.pageNumber,
          blob_id: formData.selectedDocument,
        };
      }
      await createFinding({
        projectId,
        packageId,
        finding: findingData,
      }).unwrap();
      successToast({ message: 'Successfully created finding' });
      return true;
    } catch {
      errorToast({ message: 'Failed to create finding. Please try again.' });
      return false;
    }
  };

  const handleReset = () => {
    setFormData({
      findingType: formData.findingType,
      selectedDocument: null,
      textInDocument: null,
      pageNumber: null,
      conflictMessage: null,
      conflictMessageEn: null,
      conflictType: null,
    });
    setErrors({});
  };

  const updateFormData = (field: keyof FindingFormData, value: unknown) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }

    // Validate the field in real-time
    const validation = validateFindingForm(newFormData);
    if (validation.errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: validation.errors[field] }));
    }
  };

  const findingTypeOptions = [
    { label: 'Document Finding', value: 'DOCUMENT' },
    { label: 'Project Finding', value: 'PROJECT' },
  ];

  return (
    <SheetForm
      title="Create Finding"
      description="Create a new Human Intelligence finding"
      trigger={<Button>Create Finding</Button>}
      onSubmit={handleSubmit}
      onReset={handleReset}
      submitLabel="Create Finding"
      resetLabel="Reset"
      showReset
    >
      <Field label="Finding type" htmlFor="finding" className="flex flex-col gap-4">
        <Select
          placeholder="Select Finding Type"
          options={findingTypeOptions}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
          value={findingTypeOptions.find((option) => option.value === formData.findingType)}
          onValueChange={(value) => updateFormData('findingType', value.value)}
          className="w-full"
        />
        {errors.findingType && <div className="text-sm text-red-600">{errors.findingType}</div>}
        {formData.findingType === 'DOCUMENT' ? (
          <DocumentFindingForm
            projectId={projectId}
            packageId={packageId}
            formData={formData}
            errors={errors}
            updateFormData={updateFormData}
          />
        ) : (
          <ConflictFieldset
            legend="Project Finding"
            formData={formData}
            errors={errors}
            updateFormData={updateFormData}
          />
        )}
      </Field>
    </SheetForm>
  );
};
